import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Check, X, Trash2, Pencil, Briefcase, Mail, Building2, Handshake } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

const CONTRACT_OPTIONS = [
  { value: 'full_time',  label: 'Full Time'   },
  { value: 'part_time',  label: 'Part Time'   },
  { value: 'fixed_term', label: 'Fixed Term'  },
  { value: 'contract',   label: 'Contract'    },
  { value: 'internship', label: 'Internship'  },
];

const STATUS_COLOR = {
  pending:  'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

export default function AdminJobsTab() {
  const [tab, setTab] = useState('pending');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/admin/jobs${tab === 'all' ? '' : `?status=${tab}`}`;
      const r = await fetch(url, { credentials: 'include' });
      if (r.ok) setJobs(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [tab]);

  useEffect(() => {
    // Pre-fetch link options for the edit form
    fetch(`${BACKEND_URL}/api/companies`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => setCompanies(Array.isArray(list) ? list : (list.items || [])))
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/partners`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => setPartners(Array.isArray(list) ? list : (list.items || [])))
      .catch(() => {});
  }, []);

  const setStatus = async (job, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/jobs/${job.job_id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(status === 'approved' ? 'Job published' : `Job ${status}`);
      load();
    } catch { toast.error('Could not update'); }
  };

  const del = async (job) => {
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/jobs/${job.job_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Deleted');
      load();
    } catch { toast.error('Could not delete'); }
  };

  const saveEdit = async () => {
    const payload = { ...editing };
    delete payload.job_id;
    delete payload.status;
    delete payload.posted_at;
    delete payload.approved_at;
    delete payload.submitted_by;
    delete payload.submitter_name;
    delete payload.submitter_email;
    delete payload.contract_type_label;
    delete payload.company_name;
    delete payload.company_logo;
    delete payload.sponsor_name;
    delete payload.sponsor_logo;
    if (!payload.company_id) delete payload.company_id;
    if (!payload.sponsor_id) delete payload.sponsor_id;
    if (!payload.closing_date) delete payload.closing_date;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/jobs/${editing.job_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Saved');
      setEditing(null);
      load();
    } catch { toast.error('Could not save'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Briefcase className="h-5 w-5" /> Jobs Board</h2>
          <p className="text-xs text-slate-500">Approve member-submitted job posts. Approved posts appear on the public /jobs page until their closing date.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-jobs-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-jobs-tab-approved">Live</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="admin-jobs-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="admin-jobs-tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
          ) : jobs.length === 0 ? (
            <Card className="p-8 text-center text-sm text-slate-400">No jobs in this view.</Card>
          ) : (
            jobs.map(job => (
              <Card key={job.job_id} className="p-4" data-testid={`admin-job-row-${job.job_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-slate-800">{job.title}</h3>
                      <Badge className={`text-[10px] ${STATUS_COLOR[job.status] || ''}`}>{job.status}</Badge>
                      <Badge variant="secondary" className="text-[10px]">{job.contract_type_label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      {job.location} · Submitted by {job.submitter_name || 'Unknown'} ({job.submitter_email})
                    </p>
                    {(job.company_name || job.sponsor_name) && (
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        {job.sponsor_id ? <Handshake className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                        {job.company_name || job.sponsor_name}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Mail className="h-3 w-3" /> {job.contact_email}</p>
                    {job.about && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{job.about}</p>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                    {job.status !== 'approved' && (
                      <Button size="sm" onClick={() => setStatus(job, 'approved')} data-testid={`approve-job-${job.job_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {job.status !== 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(job, 'rejected')} data-testid={`reject-job-${job.job_id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                        <X className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...job })} data-testid={`edit-job-${job.job_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(job)} data-testid={`delete-job-${job.job_id}`} className="text-rose-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Job</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Title</Label><Input value={editing.title || ''} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label className="text-xs">Location</Label><Input value={editing.location || ''} onChange={e => setEditing({ ...editing, location: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Contact email</Label><Input value={editing.contact_email || ''} onChange={e => setEditing({ ...editing, contact_email: e.target.value })} /></div>
                <div><Label className="text-xs">Salary</Label><Input value={editing.salary || ''} onChange={e => setEditing({ ...editing, salary: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Contract type</Label>
                  <Select value={editing.contract_type} onValueChange={v => setEditing({ ...editing, contract_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTRACT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Start date</Label><Input value={editing.start_date || ''} onChange={e => setEditing({ ...editing, start_date: e.target.value })} /></div>
                <div><Label className="text-xs">Closing date</Label><Input type="date" value={editing.closing_date || ''} onChange={e => setEditing({ ...editing, closing_date: e.target.value })} /></div>
              </div>
              <div>
                <Label className="text-xs">About the role</Label>
                <Textarea rows={2} value={editing.about || ''} onChange={e => setEditing({ ...editing, about: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea rows={6} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Company</Label>
                  <Select value={editing.company_id || 'none'} onValueChange={v => setEditing({ ...editing, company_id: v === 'none' ? '' : v, sponsor_id: v !== 'none' ? '' : editing.sponsor_id })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Partner</Label>
                  <Select value={editing.sponsor_id || 'none'} onValueChange={v => setEditing({ ...editing, sponsor_id: v === 'none' ? '' : v, company_id: v !== 'none' ? '' : editing.company_id })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {partners.map(p => <SelectItem key={p.sponsor_id} value={p.sponsor_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
